import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import UserActivitySection from './UserActivitySection';
import { canOffboard } from './leavers';

/**
 * Sliding detail drawer for the currently selected user.
 *
 * Commit 1 establishes the shell: slide animation, focus management, Esc to
 * close, mobile backdrop click, and reduced-motion fallback. The body is a
 * children slot that commits 2-4 fill with status header, departments
 * combobox, and the tools panel v2.
 *
 * Props:
 *   user            selected user object | null
 *   onClose         () => void
 *   children        drawer body content
 */
export default function UserDetailDrawer({ user, onClose, children }) {
  const closeBtnRef = useRef(null);
  const lastFocusRef = useRef(null);
  // We keep the previously-rendered user around so the drawer body fades out
  // gracefully rather than blanking before the slide-out finishes.
  const [retainedUser, setRetainedUser] = useState(user);
  const isOpen = !!user;

  useEffect(() => {
    if (user) setRetainedUser(user);
  }, [user]);

  // Focus management
  useEffect(() => {
    if (!isOpen) return;
    lastFocusRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const el = lastFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [isOpen]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`ut-drawer-backdrop ${isOpen ? 'is-open' : ''}`}
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className={`ut-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={retainedUser ? `Details for ${retainedUser.name}` : 'User details'}
        aria-hidden={!isOpen}
      >
        <DrawerHeader user={retainedUser} closeBtnRef={closeBtnRef} onClose={onClose} />
        <div className="ut-drawer-body">
          {retainedUser ? <DrawerOffboard user={retainedUser} /> : null}
          {retainedUser ? <DrawerHrSection user={retainedUser} /> : null}
          {retainedUser ? children : null}
          {retainedUser ? <UserActivitySection user={retainedUser} /> : null}
        </div>
      </aside>
    </>
  );
}

// Entry point to a leaver's Connected apps checklist, admins included: the
// server lets connected_apps.view holders open a leaving admin's profile.
function DrawerOffboard({ user }) {
  if (!canOffboard(user)) return null;
  return (
    <section className="ut-offboard" aria-label="Offboarding">
      <Link className="btn btn-secondary btn-small" to={`/profile/${user.id}?tab=connected-apps`}>Offboard apps</Link>
    </section>
  );
}

const HR_TITLE_CASE = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Read-only Odoo HR summary. Admin-only surface — NEVER flag-gated.
function DrawerHrSection({ user }) {
  const rows = [
    ['Job position', user.odoo_job_title],
    ['Manager', user.odoo_manager_name],
    ['Marital status', HR_TITLE_CASE(user.odoo_marital)],
    ['Mobile', user.odoo_mobile],
  ].filter(([, v]) => v != null && v !== '');

  return (
    <section style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#2d3748' }}>
        Employment (HR)
      </h3>
      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: '#a0aec0' }}>No HR details synced</p>
      ) : (
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt style={{ fontSize: 12, color: '#718096' }}>{label}</dt>
              <dd style={{ margin: 0, fontSize: 13, color: '#2d3748' }}>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </section>
  );
}

function DrawerHeader({ user, closeBtnRef, onClose }) {
  // Minimal sticky header. Commit 2 replaces this with the full status-chip
  // cluster, primary action, and overflow menu.
  if (!user) return null;
  const initials = (user.name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky',
        top: 0,
        background: 'white',
        zIndex: 1,
      }}
    >
      <button
        ref={closeBtnRef}
        type="button"
        onClick={onClose}
        aria-label="Close user details"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 22,
          lineHeight: 1,
          color: '#4a5568',
        }}
      >
        ×
      </button>
      {user.photo_url ? (
        <img
          src={user.photo_url}
          alt=""
          referrerPolicy="no-referrer"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1E3A6B 0%, #0F1E3C 100%)',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {initials}
        </span>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.name}
        </div>
        <div style={{ fontSize: 12, color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.email}
        </div>
      </div>
    </header>
  );
}
